import { getFragmentData, gql, type FragmentType } from '@plato/app-core/gql';
import type { CustomerStatus } from '@plato/app-core/gql/schema-types';
import { useInsightTypeLabel } from '@plato/app-shared/hooks/use-insight-type-label';
import {
    AppliedFilterList,
    type AppliedFilter,
} from '@plato/ui-legacy/components/filters/applied-filter-list';
import { useMemo, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { useCustomerListParams } from '../hooks/use-customer-list-params.ts';

const QUERY_FRAGMENT = gql(/* GraphQL */ `
    fragment CustomerListAppliedFilters_Query on Query {
        appliedCustomers: customers(
            filter: { ids: $appliedCustomerIds }
            sortBy: { by: { field: customer_name }, direction: asc }
        ) @include(if: $includeAppliedCustomers) {
            nodes {
                id
                name
            }
        }
        appliedSalesReps: salesReps(ids: $appliedSalesRepIds) @include(if: $includeAppliedSalesReps) {
            id
            name
        }
        appliedCompanies: companies(ids: $appliedCompanyIds) @include(if: $inlcudeAppliedCompanies) {
            id
            name
        }
    }
`);

const statusLabelKeys: Record<CustomerStatus, string> = {
    new: 'status_new',
    active: 'status_active',
    at_risk: 'status_at_risk',
    dormant: 'status_dormant',
    inactive: 'status_inactive',
    blocked: 'status_blocked',
};

type Props = {
    data: FragmentType<typeof QUERY_FRAGMENT> | undefined;
};

export function CustomerListAppliedFilters({ data: dataProp }: Props): ReactElement {
    const { t } = useTranslation('customer-list');
    const insightTypeLabel = useInsightTypeLabel();
    const { params, setParams } = useCustomerListParams();

    const data = getFragmentData(QUERY_FRAGMENT, dataProp);

    const appliedFilters = useMemo(
        () =>
            [
                ...(params.insightTypes ?? []).map((value) => {
                    return {
                        key: `insightTypes-${value}`,
                        category: t('filter_menu_title__insights'),
                        filter: insightTypeLabel(value) ?? value,
                        onRemove: () => {
                            setParams({
                                ...params,
                                insightTypes: params.insightTypes?.filter((type) => type !== value),
                            });
                        },
                    };
                }),
                ...(params.salesRepIds ?? []).map((value) => {
                    const salesRep = data?.appliedSalesReps?.find((rep) => rep?.id === value);

                    return {
                        key: `salesRepIds-${value}`,
                        category: t('filter_menu_title__sales_rep'),
                        filter: salesRep?.name ? `${value}: ${salesRep.name}` : value,
                        onRemove: () => {
                            setParams({
                                ...params,
                                salesRepIds: params.salesRepIds?.filter((id) => id !== value),
                            });
                        },
                    };
                }),
                ...(params.statuses ?? []).map((value) => ({
                    key: `statuses-${value}`,
                    category: t('filter_menu_title__status'),
                    filter: t(statusLabelKeys[value]),
                    onRemove: () => {
                        setParams({
                            ...params,
                            statuses: params.statuses?.filter((status) => status !== value),
                        });
                    },
                })),
                ...(params.cityNames ?? []).map((value) => ({
                    key: `cityNames-${value}`,
                    category: t('filter_menu_title__city'),
                    filter: value,
                    onRemove: () => {
                        setParams({
                            ...params,
                            cityNames: params.cityNames?.filter((city) => city !== value),
                        });
                    },
                })),
                ...(params.zipCodes ?? []).map((value) => ({
                    key: `zipCodes-${value}`,
                    category: t('filter_menu_title__zip_code'),
                    filter: value,
                    onRemove: () => {
                        setParams({
                            ...params,
                            zipCodes: params.zipCodes?.filter((zipCode) => zipCode !== value),
                        });
                    },
                })),
                ...(params.salesRegions ?? []).map((value) => ({
                    key: `salesRegions-${value}`,
                    category: t('filter_menu_title__sales_region'),
                    filter: value,
                    onRemove: () => {
                        setParams({
                            ...params,
                            salesRegions: params.salesRegions?.filter((region) => region !== value),
                        });
                    },
                })),
                ...(params.ids ?? []).map((value) => ({
                    key: `ids-${value}`,
                    category: t('filter_menu_title__customer'),
                    filter:
                        data?.appliedCustomers?.nodes.find((customer) => customer.id === value)?.name ??
                        value,
                    onRemove: () => {
                        setParams({ ...params, ids: params.ids?.filter((id) => id !== value) });
                    },
                })),
                ...(params.customerClasses ?? []).map((value) => ({
                    key: `customerClasses-${value}`,
                    category: t('filter_menu_title__customer_class'),
                    filter: value,
                    onRemove: () => {
                        setParams({
                            ...params,
                            customerClasses: params.customerClasses?.filter(
                                (customerClass) => customerClass !== value,
                            ),
                        });
                    },
                })),
                ...(params.industryNames ?? []).map((value) => ({
                    key: `industryNames-${value}`,
                    category: t('filter_menu_title__industry'),
                    filter: value,
                    onRemove: () => {
                        setParams({
                            ...params,
                            industryNames: params.industryNames?.filter((industry) => industry !== value),
                        });
                    },
                })),
                ...(params.companyIds ?? []).map((value) => ({
                    key: `companyIds-${value}`,
                    category: t('filter_menu_title__company'),
                    filter: data?.appliedCompanies?.find((company) => company?.id === value)?.name ?? value,
                    onRemove: () => {
                        setParams({ ...params, companyIds: params.companyIds?.filter((id) => id !== value) });
                    },
                })),
            ] satisfies AppliedFilter[],
        [params, setParams, data, t, insightTypeLabel],
    );

    return (
        <AppliedFilterList
            filters={appliedFilters}
            onRemoveAllFilters={() => {
                setParams((current) => ({ sorting: current.sorting }));
            }}
        />
    );
}
